import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, FONT_FAMILIES, FONT_WEIGHTS, GAPS, RADII, SPACINGS } from "../../common/consts";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import Input from "../DataInput/Input";
import { BlockBone, PillBone, TextBone } from "../Display/Skeletons";
import { SettingsFooter } from "../Settings";
import {
  FieldMappingAddRow,
  FieldMappingEmptyRow,
  FieldMappingRowSkeleton,
  FieldMappingTable,
  FieldMappingValueRow,
  MappingSystem,
} from "../Settings/FieldMapping";
import { HUBSPOT_SYSTEM, LINKEDIN_SYSTEM, OUTREACH_SYSTEM, SALESFORCE_SYSTEM } from "../Settings/systems";
import { createExportField, ExportField, getExportSettings, listExportFields, listExportSequences, updateExportSettings } from "../../services/firebase";
import Dialog from "../Display/Dialog";
import SearchableSelect, { SearchableSelectOption } from "../DataInput/SearchableSelect";
import { useIsHubSpotCrm } from "../CrmUi/useCrm";
import { incompleteRules, RulesSummary, SequenceOption, SequenceRoutingEditor, TagRulesEditor } from "./ExportRules";
import { CollapseToggle } from "../Settings/Collapsible";
import PlanUpgradePrompt from "../Billing/PlanUpgradePrompt";
import { ExportDatapoint, ExportSettingsResult, ExportMappings } from "../../types/addons";
import { HUBSPOT_TILE } from "../HubSpotUi/theme";
import ExportDefaultStageEditor from "./ExportDefaultStage";

const Card = styled("div")`
  border-radius: ${RADII.md};
  box-shadow: rgba(69, 66, 98, 0.16) 0 0 1px, rgba(69, 66, 98, 0.16) 0 1px 2px;
  background-color: var(--color-backgroundWhite, ${COLORS.backgroundWhite});
  padding: ${SPACINGS.xl};
  max-width: 900px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
  ${HUBSPOT_TILE}
`;

/* The page's blocks (settings card, mapping, tags, routing) one under another, at the cards' width. */
const Stack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${SPACINGS.xxl};
  max-width: 900px;
`;

const AddField = styled("div")`
  display: flex;
  gap: ${GAPS.sm};
  align-items: center;
  width: 100%;

  > button {
    flex: 0 0 auto;
  }
`;

const Title = styled("div")`
  font-family: ${FONT_FAMILIES.titles};
  font-weight: ${FONT_WEIGHTS.semibold};
  font-size: 1rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
`;

const Muted = styled(Text)`
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  margin: 0;
`;

/* Label and caption take the row; the switch or button stays at the right. */
const Row = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GAPS.md};

  > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
  /* The Toggle's wrapper is a full-width flex row of its own. */
  > :last-child:not(:first-child) {
    flex: 0 0 auto;
    width: auto;
  }
`;

const Field = styled("div")`
  max-width: 360px;
`;

type Destination = "outreach" | "hubspot" | "salesforce";
type RecordKind = string;

const RECORD_KINDS: Record<Destination, RecordKind[]> = {
  outreach: ["prospect", "account"],
  hubspot: ["contact", "company"],
  salesforce: ["contact", "lead", "account"],
};

const DESTINATION_LABELS: Record<Destination, string> = {
  outreach: "Outreach",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
};

const DESTINATION_SYSTEMS: Record<Destination, MappingSystem> = {
  outreach: OUTREACH_SYSTEM,
  hubspot: HUBSPOT_SYSTEM,
  salesforce: SALESFORCE_SYSTEM,
};

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/** Records that hold a company; the rest hold a person. */
const COMPANY_RECORDS = ["account", "company"];

/** The "+ Create new" entry in a field picker. */
const CREATE = "__create__";

/* HubSpot property types the export can write into. */
const HUBSPOT_TYPES = [
  { value: "text", label: "Single-line text" },
  { value: "textarea", label: "Multi-line text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "bool", label: "Yes / No" },
];

const DialogBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
  min-width: 360px;

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

/* "addressCity" reads as "Address city", with its API name kept under it. */
/* Fields whose API name reads badly as a label. */
const FIELD_LABELS: Record<string, string> = { hubspot_owner_id: "Owner" };

const fieldLabel = (field: string) =>
  FIELD_LABELS[field] ??
  capitalize(
    field
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\blinked in\b/g, "LinkedIn")
      .replace(/\burl\b/g, "URL")
      .replace(/\bid\b/g, "ID")
  );

/**
 * The export card as it lands: the heading, the three switch rows, the
 * destination and record selects, and the mapping table underneath.
 */
export const ExportSectionSkeleton: FC = () => {
  // The org's own CRM in the table's band: HubSpot orgs saw Outreach's while it loaded.
  const isHubSpot = useIsHubSpotCrm();
  return (
  <Stack aria-busy="true">
    <Card>
      <div>
        <TextBone width={96} size="1rem" />
        <TextBone width="72%" size="0.875rem" />
      </div>

      {[
        { label: 280, caption: "68%" },
        { label: 250, caption: "74%" },
        { label: 360, caption: "56%" },
      ].map((row, i) => (
        <Row key={i}>
          <div style={{ flex: "1 1 auto" }}>
            <TextBone width={row.label} size="0.875rem" />
            <TextBone width={row.caption} size="0.875rem" />
          </div>
          <PillBone width={48} height={24} />
        </Row>
      ))}
    </Card>

    <div>
      <FieldMappingTable
        title="Field mapping"
        description="Which part of the LinkedIn profile lands in each field. Wrap a value in braces to write it literally."
        source={LINKEDIN_SYSTEM}
        destination={isHubSpot ? HUBSPOT_SYSTEM : OUTREACH_SYSTEM}
        middleLabel="Writes"
        actions={<BlockBone width={156} height={36} radius={RADII.full} />}
        toolbar={
          <>
            <BlockBone height={36} />
            <BlockBone height={36} />
          </>
        }
      >
        {[0, 1, 2, 3].map((i) => (
          <FieldMappingRowSkeleton key={i} />
        ))}
      </FieldMappingTable>

      <SettingsFooter>
        <BlockBone width={88} height={36} radius={RADII.full} />
      </SettingsFooter>
    </div>
  </Stack>
  );
};

/* A setting a rep can see but not change: its state in words, where an admin has the switch. */
const State = styled(Text)`
  margin: 0;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
`;

const ReadOnlyValue = styled("div")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  color: var(--color-foregroundPrimary, ${COLORS.foregroundPrimary});
  overflow-wrap: anywhere;
`;

/** A boxed block whose heading opens and closes it. */
const CollapsibleCard: FC<{ title: string; description: React.ReactNode; children: React.ReactNode }> = ({ title, description, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card>
      <div>
        <Title>
          <CollapseToggle open={open} onToggle={() => setOpen((o) => !o)}>
            {title}
          </CollapseToggle>
        </Title>
        <Muted variant={TextVariant.sm}>{description}</Muted>
      </div>
      {open && children}
    </Card>
  );
};

const SCOPE_LABELS: Record<string, string> = {
  mobile: "Mobiles only",
  mobile_or_direct: "Mobiles and direct lines",
  all: "Any verified number",
};

/**
 * What an export does, for the whole team: whether it looks for an email,
 * whether it buys a phone number, whether it overwrites what is already
 * there, which field on the record each piece of the profile is written to,
 * and the tags and sequences people get. Admins set it; reps see it as is.
 */
const ExportSection: FC = () => {
  const [data, setData] = useState<ExportSettingsResult | null>(null);
  const [settings, setSettings] = useState<ExportMappings | null>(null);
  const [destination, setDestination] = useState<Destination>("outreach");
  const [recordKind, setRecordKind] = useState<RecordKind>("prospect");
  const [newField, setNewField] = useState("");
  /** The "+ Create new" dialog: for a new row ("" target) or to repoint an existing row. */
  const [creating, setCreating] = useState<{ target: string; label: string; type: string; source: string } | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const crm: "outreach" | "hubspot" = useIsHubSpotCrm() ? "hubspot" : "outreach";
  // The mapping opens on the organization's own CRM (a HubSpot org saw Outreach first).
  useEffect(() => {
    setDestination(crm);
    setRecordKind(RECORD_KINDS[crm][0]);
  }, [crm]);
  const [sequences, setSequences] = useState<SequenceOption[]>([]);
  const [sequencesError, setSequencesError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await getExportSettings();
      setData(r);
      setSettings(r.settings);
    } catch (e: any) {
      setMessage(e?.message || "Could not load export settings.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  // Only an admin edits routing, so only they need the sequences to pick from.
  const canEdit = data?.canEdit !== false;
  useEffect(() => {
    if (!data || !canEdit) return;
    listExportSequences()
      .then((r) => {
        setSequences(r.sequences);
        setSequencesError(r.error || null);
      })
      .catch(() => setSequencesError("Could not load sequences."));
  }, [data, canEdit]);

  // The destination's fields to pick from, per record: HubSpot properties or Outreach
  // fields, for the organization's own CRM only (other destinations type the API name).
  const [fieldLists, setFieldLists] = useState<Record<string, { fields: ExportField[]; error?: string }>>({});
  const listKey = `${destination}.${recordKind}`;
  useEffect(() => {
    if (!data || destination !== crm || fieldLists[listKey]) return;
    listExportFields(destination, recordKind)
      .then((r) => setFieldLists((prev) => ({ ...prev, [listKey]: r })))
      .catch(() => setFieldLists((prev) => ({ ...prev, [listKey]: { fields: [], error: `Could not load ${DESTINATION_LABELS[destination]} fields.` } })));
  }, [data, crm, destination, recordKind, listKey, fieldLists]);
  const fieldList = fieldLists[listKey];
  const [pickerVersion, setPickerVersion] = useState(0);
  const destinationFields = fieldList?.fields || [];

  const defaults = useMemo(() => {
    const d = (data?.defaults as any) || {};
    return (d?.[destination]?.[recordKind] || {}) as Record<string, string>;
  }, [data, destination, recordKind]);

  // What the "Writes" picker offers for this record: the person's or the company's datapoints.
  const datapoints = useMemo<ExportDatapoint[]>(() => {
    const list = (COMPANY_RECORDS.includes(recordKind) ? data?.datapoints?.company : data?.datapoints?.person) || [];
    return list.filter((d) => !d.destinations || d.destinations.includes(destination));
  }, [data, recordKind, destination]);

  const overrides = useMemo(() => {
    const s = (settings as any) || {};
    return (s?.[destination]?.[recordKind] || {}) as Record<string, string>;
  }, [settings, destination, recordKind]);

  if ((!settings || !data) && message) return <Muted variant={TextVariant.sm}>{message}</Muted>;

  if (!settings || !data) return <ExportSectionSkeleton />;

  const patch = (next: Partial<ExportMappings>) => {
    setSettings({ ...settings, ...next });
    setDirty(true);
  };

  const setMapping = (destField: string, profileField: string) => {
    const s: any = { ...settings };
    s[destination] = { ...(s[destination] || {}) };
    s[destination][recordKind] = { ...defaults, ...overrides, [destField]: profileField };
    setSettings(s);
    setDirty(true);
  };

  // A new row starts empty (written as nothing) until a value is typed in.
  const addField = (name?: string) => {
    const f = (name ?? newField).trim();
    if (!f) return;
    if (!(f in defaults) && !(f in overrides)) setMapping(f, "");
    setNewField("");
    setPickerVersion((v) => v + 1);
  };

  // Points a row at another field: the value moves over, and the old field is
  // switched off (a default stays listed as not written; an added one goes).
  const renameField = (from: string, to: string | null) => {
    const next = (to || "").trim();
    // Remounts the pickers, which otherwise keep the text typed into the one used.
    setPickerVersion((v) => v + 1);
    if (!next || next === from) return;
    const s: any = { ...settings };
    const map: Record<string, string> = { ...defaults, ...overrides };
    map[next] = map[from] ?? "";
    if (from in defaults) map[from] = "";
    else delete map[from];
    s[destination] = { ...(s[destination] || {}), [recordKind]: map };
    setSettings(s);
    setDirty(true);
  };

  const resetMappings = () => {
    const s: any = { ...settings };
    s[destination] = { ...(s[destination] || {}) };
    delete s[destination][recordKind];
    setSettings(s);
    setDirty(true);
  };

  const datapointOf = (key: string) => datapoints.find((d) => d.key === key);
  /** "LinkedIn URN" for linkedinUrn; a literal or an unlisted key reads as typed. */
  const sourceLabel = (source: string) => datapointOf(source)?.label || source;
  const sourceOptions = (current?: string): SearchableSelectOption[] => [
    ...(current && !datapointOf(current) ? [{ value: current, label: current }] : []),
    ...datapoints.map((d) => ({ value: d.key, label: d.label, secondary: d.description ? `${d.key} · ${d.description}` : d.key })),
  ];

  const openCreate = (target: string) => {
    // Repointing a row: the new field is named after what the row writes.
    const source = target ? overrides[target] ?? defaults[target] ?? "" : "";
    setCreating({ target, label: datapointOf(source)?.label || "", type: "text", source });
    setCreateError(null);
    setPickerVersion((v) => v + 1);
  };

  const submitCreate = async () => {
    if (!creating) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const field = await createExportField({
        destination,
        record: recordKind,
        label: creating.label.trim(),
        type: creating.type,
        taken: Object.keys({ ...defaults, ...overrides }),
      });
      setFieldLists((prev) => {
        const list = prev[listKey] || { fields: [] };
        if (list.fields.some((f) => f.name === field.name)) return prev;
        return { ...prev, [listKey]: { ...list, fields: [...list.fields, field] } };
      });
      if (creating.target) {
        renameField(creating.target, field.name);
      } else {
        setMapping(field.name, creating.source);
      }
      setMessage(
        destination === "outreach"
          ? `Mapped to ${field.name}. Outreach doesn't let apps name fields, so name it${creating.label.trim() ? ` "${creating.label.trim()}"` : ""} in Outreach under Settings > Fields, then Save here.`
          : `Created the ${field.label} property (${field.name}). Save to start writing it.`
      );
      setCreating(null);
    } catch (e: any) {
      setCreateError(e?.message || "Could not create the field.");
    } finally {
      setCreateBusy(false);
    }
  };

  const routing = settings.sequenceRouting?.[crm] || {};

  const save = async () => {
    const problem = incompleteRules(settings.tagRules || [], routing);
    if (problem) {
      setMessage(problem);
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const r = await updateExportSettings(settings);
      setData(r);
      setSettings(r.settings);
      setDirty(false);
      setMessage("Saved.");
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  // The defaults in order, then the fields an admin added, as added: a new row lands just above the add row.
  const fields = [...Object.keys(defaults).sort(), ...Object.keys(overrides).filter((k) => !(k in defaults))];
  const labelOf = (f: string) => destinationFields.find((p) => p.name === f)?.label || fieldLabel(f);
  /** Picker options: every field, with the ones other rows already write marked as taken. */
  const fieldOptions = (current?: string): SearchableSelectOption[] => [
    ...(current && !destinationFields.some((p) => p.name === current) ? [{ value: current, label: fieldLabel(current), secondary: current }] : []),
    ...destinationFields.map((p) => ({
      value: p.name,
      label: p.label,
      secondary: p.name,
      disabled: p.name !== current && fields.includes(p.name),
    })),
  ];
  const picksFields = canEdit && destination === crm;
  const fieldWord = destination === "hubspot" ? "property" : "field";
  const createOption: SearchableSelectOption = {
    value: CREATE,
    label: destination === "outreach" ? "+ Use a new custom field" : `+ Create new ${fieldWord}`,
    pinned: true,
  };

  /** A switch for an admin; for a rep, the same setting in words. */
  const setting = (checked: boolean, onChange: (v: boolean) => void, disabled = false) =>
    canEdit ? <Toggle checked={checked} disabled={disabled} onChange={onChange} /> : <State variant={TextVariant.sm}>{checked ? "On" : "Off"}</State>;

  return (
    <Stack>
      <Card>
        <div>
          <Title>Export</Title>
          <Muted variant={TextVariant.sm}>
            {canEdit
              ? "What happens when someone on your team exports a LinkedIn profile from the extension."
              : "What happens when you export a LinkedIn profile from the extension. Your admin sets these for the team."}
          </Muted>
        </div>
        {data?.planRequired && <PlanUpgradePrompt feature="export" what="Export" />}

        <Row>
          <div>
            <Text variant={TextVariant.sm} style={{ margin: 0 }}>Look for an email address</Text>
            <Muted variant={TextVariant.sm}>Finds and verifies a work email during the export. On by default.</Muted>
          </div>
          {setting(settings.findEmail !== false, (v) => patch({ findEmail: v }))}
        </Row>

        <Row>
          <div>
            <Text variant={TextVariant.sm} style={{ margin: 0 }}>Look for a phone number</Text>
            <Muted variant={TextVariant.sm}>
              {data.canFindPhone
                ? "Billed per lookup. Narrowed by the title and owner rules under Phone enrichment."
                : "Needs the phone enrichment add-on."}
            </Muted>
          </div>
          {setting(!!settings.findPhone, (v) => patch({ findPhone: v }), !data.canFindPhone)}
        </Row>

        {settings.findPhone &&
          (canEdit ? (
            <Field>
              <Select
                label="Numbers to keep on export"
                value={settings.phoneNumberScope || "mobile_or_direct"}
                onChange={(e: any) => patch({ phoneNumberScope: e?.target?.value || e })}
                options={Object.entries(SCOPE_LABELS).map(([value, label]) => ({ value, label }))}
              />
            </Field>
          ) : (
            <Row>
              <Text variant={TextVariant.sm} style={{ margin: 0 }}>Numbers kept on export</Text>
              <State variant={TextVariant.sm}>{SCOPE_LABELS[settings.phoneNumberScope || "mobile_or_direct"]}</State>
            </Row>
          ))}

        <Row>
          <div>
            <Text variant={TextVariant.sm} style={{ margin: 0 }}>Overwrite fields on records that already exist</Text>
            <Muted variant={TextVariant.sm}>Off means an export only fills fields that are empty.</Muted>
          </div>
          {setting(!!settings.overwriteExisting, (v) => patch({ overwriteExisting: v }))}
        </Row>

        {crm === "outreach" && (
          <Row>
            <div>
              <Text variant={TextVariant.sm} style={{ margin: 0 }}>Tag prospects with who exported them</Text>
              <Muted variant={TextVariant.sm}>
                {"Adds the tag \"boring export · rep's email\" to every exported prospect, so a rep finds everything they exported with Outreach's tag filter. Off by default."}
              </Muted>
            </div>
            {setting(settings.tagExporter === true, (v) => patch({ tagExporter: v }))}
          </Row>
        )}

        {crm === "hubspot" && (
          <Row>
            <div>
              <Text variant={TextVariant.sm} style={{ margin: 0 }}>Your exports in HubSpot</Text>
              <Muted variant={TextVariant.sm}>
                {data.myExports?.url
                  ? `${data.myExports.name}: an active list of every contact you exported. Sort or filter it by "Exported on (boring)" for any day.`
                  : "Your first export makes an active list of every contact you export, filtered on \"Exported by (boring)\"."}
              </Muted>
            </div>
            {data.myExports?.url && (
              <a href={data.myExports.url} target="_blank" rel="noopener noreferrer" style={{ whiteSpace: "nowrap" }}>
                Open list
              </a>
            )}
          </Row>
        )}
      </Card>

      {/* Fields only, so no box: the table and the Save that covers both. */}
      <div>
        <FieldMappingTable
          collapsible
          title="Field mapping"
          description={
            !canEdit
              ? "Which part of the LinkedIn profile lands in each field, for everyone on your team."
              : destination === "hubspot"
                ? "Which part of the LinkedIn profile lands in each property, for everyone on your team: names, LinkedIn URN and member ID, Sales Navigator ID, the exporting rep and more. Type a value in braces to write it literally, and leave a property empty to not write it."
                : "Which part of the LinkedIn profile lands in each field, for everyone on your team: names, LinkedIn URN and member ID, Sales Navigator ID, the exporting rep and more. Type a value in braces to write it literally, and leave a field empty to not write it."
          }
          source={LINKEDIN_SYSTEM}
          destination={DESTINATION_SYSTEMS[destination]}
          middleLabel="Writes"
          actions={
            canEdit && (
              <Button variant={ButtonVariants.light} type="button" onClick={resetMappings}>
                Reset to defaults
              </Button>
            )
          }
          toolbar={
            <>
              <Select
                fullWidth
                label="Destination"
                value={destination}
                onChange={(e: any) => {
                  const next = (e?.target?.value || e) as Destination;
                  setDestination(next);
                  setRecordKind(RECORD_KINDS[next][0]);
                }}
                options={(Object.keys(RECORD_KINDS) as Destination[]).map((d) => ({ value: d, label: DESTINATION_LABELS[d] }))}
              />
              <Select
                fullWidth
                label="Record"
                value={recordKind}
                onChange={(e: any) => setRecordKind(e?.target?.value || e)}
                options={RECORD_KINDS[destination].map((k) => ({ value: k, label: capitalize(k) }))}
              />
            </>
          }
        >
          {fields.length === 0 ? (
            <FieldMappingEmptyRow>No default mapping for this record yet.</FieldMappingEmptyRow>
          ) : (
            fields.map((f) => {
              const source = overrides[f] ?? defaults[f] ?? "";
              const literal = source.startsWith("{") && source.endsWith("}");
              return (
                <FieldMappingValueRow
                  key={f}
                  source={
                    canEdit ? (
                      datapoints.length ? (
                        <SearchableSelect
                          key={`${pickerVersion}.${f}`}
                          fullWidth
                          freeSolo
                          placeholder={defaults[f] ? sourceLabel(defaults[f]) : "Pick a datapoint, or type {text}"}
                          value={source || undefined}
                          onChange={(v) => setMapping(f, v || "")}
                          options={sourceOptions(source)}
                        />
                      ) : (
                        <Input
                          fullWidth
                          value={source}
                          placeholder={defaults[f] || "profile field"}
                          onChange={(e: any) => setMapping(f, e.target.value)}
                        />
                      )
                    ) : (
                      <ReadOnlyValue>{source ? sourceLabel(source) : "Not written"}</ReadOnlyValue>
                    )
                  }
                  status={!source ? "unmapped" : literal ? "fixed" : "writes"}
                  destinationName={DESTINATION_LABELS[destination]}
                  label={labelOf(f)}
                  detail={f}
                  target={
                    picksFields && fieldList ? (
                      <SearchableSelect
                        key={pickerVersion}
                        fullWidth
                        value={f}
                        onChange={(v) => (v === CREATE ? openCreate(f) : renameField(f, v))}
                        options={[...fieldOptions(f), createOption]}
                      />
                    ) : undefined
                  }
                />
              );
            })
          )}
          {canEdit && (
            <FieldMappingAddRow label={`Add ${destination === "outreach" ? "an" : "a"} ${DESTINATION_LABELS[destination]} ${recordKind} ${fieldWord}`}>
              {picksFields ? (
                <SearchableSelect
                  key={`add.${pickerVersion}`}
                  fullWidth
                  freeSolo
                  placeholder={fieldList ? `Choose a ${fieldWord}` : "Loading…"}
                  loading={!fieldList}
                  value={undefined}
                  onPick={(v) => (v === CREATE ? openCreate("") : addField(v))}
                  options={[createOption, ...fieldOptions().filter((o) => !o.disabled)]}
                />
              ) : (
                <AddField>
                  <Input
                    fullWidth
                    size="small"
                    placeholder={`${DESTINATION_LABELS[destination]} field's API name`}
                    value={newField}
                    onChange={(e: any) => setNewField(e.target.value)}
                    onKeyDown={(e: any) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addField();
                      }
                    }}
                  />
                  <Button variant={ButtonVariants.light} type="button" disabled={!newField.trim()} onClick={() => addField()}>
                    Add
                  </Button>
                </AddField>
              )}
            </FieldMappingAddRow>
          )}
        </FieldMappingTable>
      </div>

      <CollapsibleCard
        title="Default stage"
        description={
          crm === "hubspot"
            ? "The lifecycle stage or lead status a contact gets when an export creates it, and when an export updates a contact already in HubSpot."
            : "The stage a prospect gets when an export creates it, and when an export updates a prospect already in Outreach."
        }
      >
        <ExportDefaultStageEditor
          crm={crm}
          canEdit={canEdit}
          value={settings.defaultStage?.[crm]}
          onChange={(next) => patch({ defaultStage: { ...(settings.defaultStage || {}), [crm]: next } })}
        />
      </CollapsibleCard>

      <CollapsibleCard
        title="Tags"
        description={
          crm === "hubspot"
            ? "Puts exported contacts in static lists of these names (made when missing) when a rule matches. Every matching rule applies."
            : "Adds tags to exported prospects when a rule matches. Every matching rule applies, and existing tags are kept."
        }
      >
        {canEdit ? (
          <TagRulesEditor crm={crm} rules={settings.tagRules || []} onChange={(tagRules) => patch({ tagRules })} />
        ) : (
          <RulesSummary tagRules={settings.tagRules || []} crm={crm} />
        )}
      </CollapsibleCard>

      <CollapsibleCard
        title="Sequence routing"
        description={`Which ${crm === "hubspot" ? "HubSpot" : "Outreach"} sequence an exported person goes into. The first matching rule wins.`}
      >
        {canEdit ? (
          <SequenceRoutingEditor
            routing={routing}
            onChange={(next) => patch({ sequenceRouting: { ...(settings.sequenceRouting || {}), [crm]: next } })}
            sequences={sequences}
            sequencesError={sequencesError}
            defaultLabel="When no rule matches, add them to"
          />
        ) : (
          <RulesSummary routing={routing} crm={crm} />
        )}
      </CollapsibleCard>

      {creating && (
        <Dialog
          open
          onClose={() => !createBusy && setCreating(null)}
          title={destination === "outreach" ? `Use a new Outreach ${recordKind} field` : `Create a HubSpot ${recordKind} property`}
          maxWidth={480}
          primaryButtonProps={{
            children: createBusy ? "Creating…" : destination === "outreach" ? "Use a free field" : "Create property",
            onClick: submitCreate,
            disabled: createBusy || (destination === "hubspot" && !creating.label.trim()),
          }}
          secondaryButtonProps={{ children: "Cancel", onClick: () => setCreating(null), disabled: createBusy }}
        >
          <DialogBody>
            <Muted variant={TextVariant.sm}>
              {destination === "outreach"
                ? "Outreach doesn't let apps create fields, so boring takes the first custom field that has no name in Outreach and that nothing in boring writes. Name it in Outreach afterwards so your team can see it."
                : `boring adds the property to your HubSpot ${recordKind === "company" ? "companies" : "contacts"} and maps it here.`}
            </Muted>
            {!creating.target && datapoints.length > 0 && (
              <SearchableSelect
                fullWidth
                label="Writes"
                placeholder="Pick a datapoint"
                value={creating.source || undefined}
                onChange={(v) => {
                  const key = v || "";
                  setCreating((c) => (c ? { ...c, source: key, label: c.label || datapointOf(key)?.label || "" } : c));
                }}
                options={sourceOptions()}
              />
            )}
            <Input
              fullWidth
              label={destination === "outreach" ? "Name to give it in Outreach" : "Property name"}
              value={creating.label}
              placeholder={destination === "outreach" ? "e.g. Sales Navigator ID" : "e.g. LinkedIn URN"}
              onChange={(e: any) => setCreating((c) => (c ? { ...c, label: e.target.value } : c))}
              disabled={createBusy}
              autoFocus
            />
            {destination === "hubspot" && (
              <Select
                fullWidth
                label="Type"
                value={creating.type}
                onChange={(e: any) => setCreating((c) => (c ? { ...c, type: e?.target?.value || e } : c))}
                options={HUBSPOT_TYPES}
              />
            )}
            {createError && <Muted variant={TextVariant.sm} style={{ color: `var(--color-statusDangerText, ${COLORS.statusDangerText})` }}>{createError}</Muted>}
          </DialogBody>
        </Dialog>
      )}

      {canEdit && (
        <div>
          <SettingsFooter start={message && <Muted variant={TextVariant.sm}>{message}</Muted>}>
            <Button variant={ButtonVariants.brand} type="button" disabled={!dirty || saving} loading={saving} onClick={save}>
              Save
            </Button>
          </SettingsFooter>
        </div>
      )}
    </Stack>
  );
};

export default ExportSection;
